import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tree,
  Typography,
  Upload,
  message
} from "antd";
import { FileTextOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { isAuthenticated } from "../../../commons/Auth/auth";
import CustomerSelect from "../../../commons/Billing/CustomerSelect";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { ACCEPT, errorText, importGaeb, pdfStatus, previewGaeb } from "./api";
import GaebTags from "./GaebTags";
import "./gaeb.css";

const { Text, Paragraph } = Typography;

const qty = n =>
  n === null || n === undefined
    ? ""
    : Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 });
const money = n =>
  n === null || n === undefined
    ? ""
    : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });

/** Preview tree → antd Tree data (titles expanded, positions with OZ, quantity, kind). */
const treeData = (nodes, t, prefix = "n") =>
  nodes.map((node, i) => {
    const key = `${prefix}-${i}`;
    if (node.type === "title")
      return {
        key,
        title: (
          <span className="gaeb-tree__title">
            <strong>{node.oz}</strong> {node.label}
          </span>
        ),
        children: treeData(node.children || [], t, key)
      };
    if (node.type === "item")
      return {
        key,
        isLeaf: true,
        title: (
          <span className="gaeb-tree__item">
            <span className="gaeb-tree__line">
              <Text strong>{node.oz}</Text>{" "}
              <span>{node.shortText || node.longText?.split("\n")[0]}</span>
            </span>
            <span className="gaeb-tree__meta">
              {node.freeQty ? t("Quantity by bidder") : `${qty(node.qty)} ${node.unit || ""}`}
              {node.unitPrice !== null && node.unitPrice !== undefined
                ? ` · ${t("Unit price")} ${money(node.unitPrice)}`
                : ""}
              <GaebTags item={{ gaeb: node }} />
            </span>
          </span>
        )
      };
    return {
      key,
      isLeaf: true,
      title: (
        <span className="gaeb-tree__note">
          <FileTextOutlined /> {node.type === "perf" ? t("Execution description") : t("Note")}
          {node.perfNo ? ` ${node.perfNo}` : ""}: {node.label || node.text?.split("\n")[0]}
        </span>
      )
    };
  });

const titleKeys = (nodes, prefix = "n") =>
  nodes.flatMap((node, i) =>
    node.type === "title"
      ? [`${prefix}-${i}`, ...titleKeys(node.children || [], `${prefix}-${i}`)]
      : []
  );

const POLL_MS = 3000;
const POLL_LIMIT_MS = 10 * 60 * 1000;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * "Import GAEB / PDF" on the offers list: upload (.x83/.x81/.d83/.p83/.xml … or a PDF) → preview
 * of the LV (titles, positions, warnings) → customer and title → draft offer. A PDF is read by
 * the server in the background (utils/gaeb/pdf.js); the dialog polls until it is done.
 */
const GaebImport = ({ updateParent }) => {
  const { t, i18n } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const auth = isAuthenticated();
  const serviceProviderId = auth?.employer?._id ? auth.employer._id : auth?._id;
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [readingPdf, setReadingPdf] = useState(false);
  // bumped on every new pick and on close, so a running poll stops and drops its result
  const run = useRef(0);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [choice, setChoice] = useState({
    client: null,
    clientBranch: null,
    createClient: false,
    title: ""
  });

  useEffect(() => {
    if (!open || customers.length) return;
    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/users/clients/limited`,
        { pageSize: 1000, pageNo: 0, serviceProvider: serviceProviderId },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      )
      .then(res => setCustomers(res.data?.data || []))
      .catch(() => setCustomers([]));
  }, [open]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError("");
    setChoice({ client: null, clientBranch: null, createClient: false, title: "" });
  };

  const close = () => {
    run.current += 1;
    setOpen(false);
    setLoading(false);
    setReadingPdf(false);
    reset();
  };

  const pick = async picked => {
    const id = (run.current += 1);
    reset();
    setFile(picked);
    setLoading(true);
    setReadingPdf(false);
    try {
      let res = await previewGaeb(picked, { language: (i18n.language || "de").slice(0, 2) });
      const started = Date.now();
      while (res.data.pdf && res.data.status === "reading") {
        if (run.current !== id) return false;
        setReadingPdf(true);
        if (Date.now() - started > POLL_LIMIT_MS)
          throw new Error(t("Reading the PDF takes too long. Please try again later."));
        await wait(POLL_MS);
        if (run.current !== id) return false;
        res = await pdfStatus(res.data.sha256);
      }
      if (run.current !== id) return false;
      const p = res.data.preview;
      setPreview(p);
      setChoice(c => ({ ...c, title: p.boq?.label || p.project?.label || p.project?.name || "" }));
    } catch (err) {
      if (run.current === id) setError(errorText(err, t("The file could not be read.")));
    } finally {
      if (run.current === id) {
        setLoading(false);
        setReadingPdf(false);
      }
    }
    return false;
  };

  const submit = async () => {
    if (!choice.client && !choice.createClient) {
      message.error(t("Please choose a customer for the offer."));
      return;
    }
    setSaving(true);
    try {
      const res = await importGaeb(file, {
        client: choice.createClient ? "" : choice.client,
        clientBranch: choice.createClient ? "" : choice.clientBranch,
        createClient: choice.createClient ? "true" : "",
        title: choice.title
      });
      message.success(
        t("Offer created with {{count}} positions", { count: res.data.offer.items.length })
      );
      close();
      updateParent?.();
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const tree = useMemo(() => (preview ? treeData(preview.tree, t) : []), [preview, t]);
  const expanded = useMemo(() => (preview ? titleKeys(preview.tree) : []), [preview]);
  const owner = preview?.award?.owner;
  // the PDF_AI note is shown as its own info box above
  const visibleWarnings = (preview?.warnings || []).filter(w => w.code !== "PDF_AI");

  return (
    <>
      <Button icon={<UploadOutlined />} onClick={() => setOpen(true)}>
        {t("Import GAEB / PDF")}
      </Button>
      <Modal
        open={open}
        title={t("Import GAEB / PDF")}
        onCancel={close}
        width={isMobile ? "100%" : 860}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={close}>
            {t("Cancel")}
          </Button>,
          <Button key="ok" type="primary" disabled={!preview} loading={saving} onClick={submit}>
            {t("Create offer")}
          </Button>
        ]}
      >
        <div className="gaeb-ui">
          <Upload.Dragger
            accept={ACCEPT}
            multiple={false}
            showUploadList={false}
            beforeUpload={pick}
            disabled={loading || saving}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p>{file ? file.name : t("Choose a GAEB or PDF file or drop it here")}</p>
            <Text type="secondary">
              {t("GAEB DA XML (X81, X83, X84, X86), GAEB 90 (D83) or GAEB 2000 (P83)")}
            </Text>
            <br />
            <Text type="secondary">
              {t("or a PDF (Leistungsverzeichnis, price request, list of work)")}
            </Text>
          </Upload.Dragger>

          {loading && (
            <div className="gaeb-ui__center">
              <Space direction="vertical" align="center">
                <Spin />
                {readingPdf && (
                  <Text type="secondary">
                    {t(
                      "Reading the positions from the PDF… This can take a minute or two for long documents."
                    )}
                  </Text>
                )}
              </Space>
            </div>
          )}
          {error && <Alert className="gaeb-ui__block" type="error" showIcon message={error} />}

          {preview && (
            <>
              <Descriptions
                className="gaeb-ui__block"
                size="small"
                column={isMobile ? 1 : 2}
                bordered
              >
                <Descriptions.Item label={t("Format")}>
                  {preview.format === "pdf" ? (
                    t("PDF (read automatically)")
                  ) : (
                    <>
                      {preview.sourceFormat}
                      {preview.version ? ` · ${preview.version}` : ""}
                      {preview.phaseName ? ` · ${preview.phaseName}` : ""}
                    </>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("Positions")}>
                  {t("{{items}} positions in {{titles}} sections", {
                    items: preview.stats.items,
                    titles: preview.stats.titles
                  })}
                </Descriptions.Item>
                {preview.project?.label || preview.project?.name ? (
                  <Descriptions.Item label={t("Project")}>
                    {preview.project.label || preview.project.name}
                  </Descriptions.Item>
                ) : null}
                {owner ? (
                  <Descriptions.Item label={t("Contracting authority")}>
                    {[
                      owner.name,
                      owner.street,
                      [owner.zipCode, owner.city].filter(Boolean).join(" ")
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Descriptions.Item>
                ) : null}
                {preview.award?.awardNo ? (
                  <Descriptions.Item label={t("Award number")}>
                    {preview.award.awardNo}
                  </Descriptions.Item>
                ) : null}
                {preview.award?.openDate ? (
                  <Descriptions.Item label={t("Submission deadline")}>
                    {preview.award.openDate.split("-").reverse().join(".")}
                    {preview.award.openTime ? ` ${preview.award.openTime.slice(0, 5)}` : ""}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>

              {preview.format === "pdf" && (
                <Alert
                  className="gaeb-ui__block"
                  type="info"
                  showIcon
                  message={t(
                    "The positions were read from the PDF automatically. Please check quantities, units and texts against the document before you send the offer."
                  )}
                />
              )}
              {preview.stats.provisional + preview.stats.alternative > 0 && (
                <Alert
                  className="gaeb-ui__block"
                  type="info"
                  showIcon
                  message={t(
                    "{{count}} contingency or alternative positions are priced but not added to the offer total.",
                    { count: preview.stats.provisional + preview.stats.alternative }
                  )}
                />
              )}
              {visibleWarnings.length > 0 && (
                <Alert
                  className="gaeb-ui__block"
                  type="warning"
                  showIcon
                  message={t("{{count}} notes on the file", { count: visibleWarnings.length })}
                  description={
                    <ul className="gaeb-ui__warnings">
                      {visibleWarnings.slice(0, 20).map((w, i) => (
                        <li key={i}>{w.message}</li>
                      ))}
                    </ul>
                  }
                />
              )}

              <div className="gaeb-ui__block gaeb-tree">
                <Tree
                  treeData={tree}
                  defaultExpandedKeys={expanded}
                  selectable={false}
                  blockNode
                  showLine
                />
              </div>

              <Form layout="vertical" className="gaeb-ui__block">
                <Form.Item label={t("Customer")} required>
                  <CustomerSelect
                    size="large"
                    placeholder={t("Select Client")}
                    customers={customers}
                    value={choice.client}
                    branch={choice.clientBranch}
                    disabled={choice.createClient}
                    onChange={(id, branchId) =>
                      setChoice(c => ({ ...c, client: id, clientBranch: branchId }))
                    }
                  />
                  {owner?.name ? (
                    <Checkbox
                      className="gaeb-ui__check"
                      checked={choice.createClient}
                      onChange={e => setChoice(c => ({ ...c, createClient: e.target.checked }))}
                    >
                      {t("Create a new customer from the contracting authority ({{name}})", {
                        name: owner.name
                      })}
                    </Checkbox>
                  ) : null}
                </Form.Item>
                <Form.Item label={t("Title")}>
                  <Input
                    size="large"
                    value={choice.title}
                    maxLength={200}
                    onChange={e => setChoice(c => ({ ...c, title: e.target.value }))}
                  />
                </Form.Item>
              </Form>
              <Paragraph type="secondary">
                {preview.format === "pdf"
                  ? t(
                      "The offer is created as a draft. Check the positions and enter your unit prices in the offer editor."
                    )
                  : t(
                      "The offer is created as a draft. Enter your unit prices in the offer editor, then export it as GAEB X84."
                    )}
              </Paragraph>
            </>
          )}
          {!preview && !loading && !error && (
            <Space direction="vertical" className="gaeb-ui__block">
              <Text type="secondary">{t("Nothing is saved until you create the offer.")}</Text>
            </Space>
          )}
        </div>
      </Modal>
    </>
  );
};

export default GaebImport;
